import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import {
  Form,
  InputButtonGroup,
  InputCheckbox,
  InputMask,
  InputText,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";

import {
  Button,
  displayToast,
  Emphasis,
  FormAction,
  FormField,
  FormGrid,
  FormNotice,
  FormRow,
  FormTitle,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { captureException } from "@sentry/react";
import axios from "axios";
import "./contactForm.scss";
import { LoaderAnimation } from "../loaderAnimation/LoaderAnimation";

function cleanMaskedInput(val: unknown) {
  if (typeof val !== "string") return undefined;
  return val.replace(/\s/g, "");
}

const YOUSIGN_NAME_REGEX =
  /^[a-zA-Z0-9àáâãäåæçèéêëìíîïñòóôõöùúûüýÿÀÁÂÃÄÅÇÈÉÊËÌÍÎÏÑÒÓÔÕÖÙÚÛÜÝ ß.’'()-]+$/;

const schema = zod.object({
  isSir: zod.boolean({ required_error: "Champ requis" }),
  name: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .max(95, "Nom trop long")
    .regex(YOUSIGN_NAME_REGEX, { message: "Nom invalide" }),
  surname: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .max(95, "Prénom trop long")
    .regex(YOUSIGN_NAME_REGEX, { message: "Prénom invalide" }),
  mail: zod
    .string({ required_error: "Champ requis" })
    .min(1, "Champ requis")
    .email({ message: "Email invalide" }),
  phoneNumber: zod.preprocess(
    cleanMaskedInput,
    zod
      .string({ required_error: "Champ requis" })
      .min(1, "Champ requis")
      .regex(/^0[1-5]\d{8}|06\d{8}|07[3-9]\d{7}$/, {
        message: "Numéro invalide",
      })
  ),
  acceptDataCollection: zod
    .boolean({ required_error: "Champ requis" })
    .refine((val) => val, "Champ requis"),
});

const getConsentLabel = () => {
  return "J'accepte le stockage et le traitement de mes données personnelles afin d'être recontacté par Tutélaire.";
};

type FormValues = zod.infer<typeof schema>;

const handleSubmit = (afterSubmit: () => void) => async (data: FormValues) => {
  try {
    await axios.post<null>(`//${process.env.VITE_URL_GATEWAY}/task/callme`, {
      civilite: data.isSir ? "Monsieur" : "Madame",
      nom: data.name,
      prenom: data.surname,
      contactMail: data.mail,
      contactTelephone: cleanMaskedInput(data.phoneNumber),
      consentements: [
        {
          description: getConsentLabel(),
          type: "CONSENTEMENT_TRAITEMENT_DONNEES",
        },
      ],
    });
  } catch (err) {
    captureException(err);
    console.error(err);
    displayToast("Une erreur inconnue est survenue", "error");
    throw err;
  }
  afterSubmit();
};

type ContactFormProps = {
  afterSubmit: () => void;
  hasTips?: boolean;
};

const getTitle = () => {
  return <>Laissez-nous vos coordonnées, un conseiller vous rappelera</>;
};

export function ContactForm({ afterSubmit, hasTips }: ContactFormProps) {
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
  });

  return (
    <div
      className="contact-form"
      data-submitting={formConfig.formState.isSubmitting}
      data-submitted={formConfig.formState.isSubmitSuccessful}
    >
      <div className="contact-form__form" data-hastips={hasTips}>
        <Form formConfig={formConfig} onSubmit={handleSubmit(afterSubmit)}>
          <FormGrid>
            <FormTitle>
              <h1>{getTitle()}</h1>
            </FormTitle>
            <FormNotice>
              <p>* Champs obligatoires.</p>
            </FormNotice>
            <FormRow>
              <FormField>
                <span className="contact-form__form-label">
                  Civilité <Emphasis>*</Emphasis>
                </span>
                <InputButtonGroup<FormValues, boolean>
                  className="contact-form__civilite-input"
                  variant="outline"
                  size="large"
                  name="isSir"
                  required
                  options={[
                    { label: "Monsieur", value: true },
                    { label: "Madame", value: false },
                  ]}
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField size="half">
                <InputText<FormValues> name="name" label="Nom" required />
              </FormField>
              <FormField size="half">
                <InputText<FormValues> name="surname" label="Prénom" required />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField size="full">
                <InputText<FormValues>
                  name="mail"
                  label="Adresse mail"
                  required
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField size="half">
                <InputMask<FormValues>
                  mask="99 99 99 99 99"
                  name="phoneNumber"
                  label="Numéro de téléphone"
                  required
                />
              </FormField>
            </FormRow>
            <FormRow>
              <FormField size="full">
                <InputCheckbox<FormValues>
                  className="contact-form__checkbox"
                  name="acceptDataCollection"
                  required
                  label={getConsentLabel()}
                />
              </FormField>
            </FormRow>
            <FormAction>
              <Button
                className="side-pane-form__button"
                variant="outline"
                type="submit"
              >
                Me faire rappeler
              </Button>
            </FormAction>
          </FormGrid>
        </Form>
        <div className="contact-form__mentions-legales">
          <p>
            <small>
              Les informations recueillies dans le présent formulaire, ont pour
              finalités de pouvoir vous rappeler et répondre à vos questions.
              Vous disposez notamment des droits d’accès, de rectification,
              d’opposition, de portabilité, d&apos;effacement et de limitation
              du traitement vous concernant. Pour l’exercice de vos droits,
              adressez un courrier simple au responsable de traitement :
              Tutélaire, 157 avenue de France, 75013 Paris ou un courriel à :
              <a
                href="mailto:donneespersonnelles@tutelaire.fr"
                target="_blank"
                rel="noreferrer"
              >
                donneespersonnelles@tutelaire.fr
              </a>
              . Toute demande doit être accompagnée d’une copie d&apos;un
              justificatif d’identité comportant la signature de son titulaire
              et doit préciser l’adresse à laquelle la réponse de Tutélaire doit
              parvenir. Dès réception de votre demande complète, nous répondrons
              dans un délai maximum de 30 jours calendaires.
              <br />
              <br />
              Pour plus d’informations relatives à la gestion de vos données,
              consultez à tout moment notre politique de confidentialité sur le
              site internet{" "}
              <a href="www.tutelaire.fr" target="_blank">
                www.tutelaire.fr
              </a>{" "}
              ou la demander par courrier simple à Tutélaire, responsable de
              traitement. Vous avez également la possibilité d’introduire une
              réclamation auprès de la Commission Nationale de l’Informatique et
              des Libertés (
              <a href="www.cnil.fr" target="_blank">
                www.cnil.fr
              </a>
              )
            </small>
          </p>
        </div>
      </div>
      <div className="contact-form__pane contact-form__submitting">
        <LoaderAnimation />
      </div>
      <div className="contact-form__pane contact-form__success">
        <img src="/check-circle-white.svg" alt="" />
        <h1>Votre demande a bien été envoyée</h1>
      </div>
    </div>
  );
}
